import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Header from './Header'
import useApi from '../hooks/useApi';
import Modal from "react-modal";
import DateTimeSelectorFavPro from './DateTimeSelectorFavPro';
import { useNotifications } from '../context/NotificationProvider';
import useAuth from '../hooks/useAuth';
import CalendarManagementService from '../services/calendar_management.service';
import GeneralSettingService from '../services/general_setting.service';
import GoogleService from '../services/google.service';
import { usePrompt } from '../hooks/usePrompt';
import { useTranslation } from 'react-i18next';
import AvailableSlotsService from '../services/available_slots.service';
import TherapistService from '../services/therapist.service';
import { bindEvent, getPusher, initPusher } from "../services/pusher.service";
import { hasAnyBookableSlot, mapWithConcurrency, normalizeAvailabilityDate, toDisplayDateString, uniqueAvailabilityDates } from '../utils/availability';


const customStyles = {
    content: {
        width: "33%",
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
    },
};

const customStyles2 = {
    content: {
        width: "33%",
        top: "50%",
        padding: "0px",
        left: "50%",
        right: "auto",
        bottom: "auto",
        borderRadius: "0px",
        boxShadow: "none",
        maxWidth: "450px",
        margin: "auto",
        textAlign: "center",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
    },
};

const EDIT_STEP_PATHS = [
    '/location-pro',
    '/fav-flypro',
    '/flypro-service',
    '/flypro-booking',
    '/payment'
];

const EDIT_SESSION_KEYS = [
    "editBookingMode",
    "editAddressMode",
    "modificationBookingId",
    "oldBooking",
    "newBooking",
    "productChanged",
    "therapistChanged",
    "addressChanged",
    "bookingToEdit",
    "state",
    "address"
];

const MAX_VISIBLE_MONTHS = 2;
const DATE_WINDOW_SIZE = 9;
const ENABLE_FLYPRO_AVAILABILITY_BATCH = process.env.REACT_APP_ENABLE_FLYPRO_AVAILABILITY_BATCH !== 'false';

const getVisibleBookingDays = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endExclusive = new Date(today.getFullYear(), today.getMonth() + MAX_VISIBLE_MONTHS + 1, 1);
    const days = [];
    let cur = new Date(today);

    while (cur < endExclusive) {
        days.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
        cur.setDate(cur.getDate() + 1);
    }

    return days;
};

const getInitialDaysToShow = () => getVisibleBookingDays().slice(0, DATE_WINDOW_SIZE);


Modal.setAppElement("#root");

function FlyProBooking() {
    const { t, i18n } = useTranslation('flyProBooking');
    const formatLocalDate = (date) =>
        date ? new Date(date).toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' }) : '';
    const [modalIsOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDateScroll, setSelectedDateScroll] = useState(new Date());
    const [selectedTime, setSelectedTime] = useState(null);
    const location = useLocation();
    const { auth } = useAuth();
    const api = useApi();
    const navigate = useNavigate();
    const [state, setState] = useState(location.state || JSON.parse(sessionStorage.getItem('state') ?? '{}'));
    const { address, selectedServices, duo, selectedTherapist, phone, origin_flow } = state;
    const { lng, lat, zip_code } = address || {};
    const notify = useNotifications();
    const [unavailableDates, setUnavailableDates] = useState([]);
    const [availableHours, setAvailableHours] = useState([]);
    const [daysToShow, setDaysToShow] = useState(() => getInitialDaysToShow());
    const [showPopup, setShowPopup] = useState(false);
    const [buffertime, setBufferTime] = useState([]);
    const [traveldistance, setTravelDistance] = useState([]);
    const totalDuration = selectedServices?.reduce((total, service) => {
        const duration = service?.product?.duration ? parseInt(service.product.duration, 10) : 0;
        return total + duration;
    }, 0);

    const totalPrice = selectedServices?.reduce((total, service) => {
        const price = service?.product?.price ? service.product.price : 0;
        return total + price;
    }, 0);

    const realtimeRefreshInFlightRef = useRef(false);
    const realtimeLastRefreshRef = useRef(0);
    const refreshAvailabilityRef = useRef(null);
    const isFetchingUnavailableDates = useRef(false);
    const prevUnavailableDeps = useRef({});
    const lastUnavailableRefreshRef = useRef(0);
    const unavailableDatesRequestRef = useRef(0);
    const bookableDatesCacheRef = useRef(new Map());
    const bookableDatesInFlightRef = useRef(new Map());
    const isFetchingAvailableHours = useRef(false);
    const prevAvailableHoursDeps = useRef({});

    const [modalIsPaiementOpen, setModalIsPaiementOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const openModalPaiement = (message) => {
        setModalMessage(message);
        setModalIsPaiementOpen(true);
    };

    const closeModalPaiement = () => {
        setModalIsPaiementOpen(false);
    };

    const redirectToHome = () => {
        // si tu utilises react-router
        navigate('/fav-flypro');
        setModalIsPaiementOpen(false);
    };

    const updateDaysToShow = useCallback((baseDate) => {
        if (!baseDate || isNaN(baseDate.getTime())) {
            console.error('Invalid baseDate in updateDaysToShow');
            return;
        }

        const allDays = getVisibleBookingDays();
        const lastAllowedDate = allDays[allDays.length - 1];
        const anchor = new Date(baseDate);
        anchor.setHours(0, 0, 0, 0);
        if (anchor < allDays[0]) anchor.setTime(allDays[0].getTime());
        if (anchor > lastAllowedDate) anchor.setTime(lastAllowedDate.getTime());
        let startIndex = allDays.findIndex(d => d.getTime() >= anchor.getTime());
        if (startIndex === -1) startIndex = Math.max(0, allDays.length - DATE_WINDOW_SIZE);

        const maxStart = Math.max(0, allDays.length - DATE_WINDOW_SIZE);
        const clampedStart = Math.min(startIndex, maxStart);
        const slice = allDays.slice(clampedStart, clampedStart + DATE_WINDOW_SIZE);
        setDaysToShow(slice);
    }, []);

    useEffect(() => {
        const now = new Date();
        const nextQuarterHour = Math.ceil(now.getMinutes() / 15) * 15;
        now.setMinutes(nextQuarterHour);
        now.setSeconds(0);
        setSelectedTime(null);
        updateDaysToShow(now);
    }, [updateDaysToShow]);

    const isEditBookingMode = sessionStorage.getItem("editBookingMode") === "true" || location?.state?.fromEditBooking;

    // Use `usePrompt` for navigation blocking
    usePrompt(
        isEditBookingMode,
        "Êtes-vous sûr de vouloir quitter la modification ? Vos changements ne seront pas enregistrés.",
        next => EDIT_STEP_PATHS.some(p => next.pathname.startsWith(p))
    );


    const calculateDistance = async () => {
        try {
            const formattedDate = formatLocalDate(selectedDate);
            console.log('selectedTime', selectedTime);
            console.log('selectedDate:', selectedDate);

            const formattedTime = selectedTime ? selectedTime : selectedDate.toLocaleTimeString('en-CA', { timeZone: 'Europe/Zurich', hour: '2-digit', minute: '2-digit', hour12: false });

            const finalFormattedDateTime = `${formattedDate} ${formattedTime}`;
            console.log('finalFormattedDateTime:', finalFormattedDateTime);

            console.log('travelDistance finalFormattedDateTime:', finalFormattedDateTime);

            // Handle both address and address.address structures
            const rawAddress = address?.lat && address?.lng
                ? address
                : address?.address && address.address.lat && address.address.lng
                    ? address.address
                    : null;

            if (!rawAddress) {
                console.error('No valid address with lat/lng found:', address);
                return;
            }

            const params = {
                latitudeA: selectedTherapist[0].primary_address.latitude,
                longitudeA: selectedTherapist[0].primary_address.longitude,
                latitudeB: rawAddress.lat,
                longitudeB: rawAddress.lng,
                departure_time: finalFormattedDateTime,
            };
            console.log('params', params);


            const response = await GoogleService.getDistanceAndTime(params, api);

            console.log('travelDistance:', response.data.duration);
            setTravelDistance(response.data.duration);
        } catch (error) {
            console.error('Error fetching distance:', error);
        }
    };

    const fetchBufferTime = async () => {
        try {
            const bufferTime = await GeneralSettingService.index('setting=in:buffer_before,buffer_after', api,
                auth?.accessToken ? false : true);

            const bufferBefore = bufferTime.find(item => item.setting === 'buffer_before')?.value || '0';
            const bufferAfter = bufferTime.find(item => item.setting === 'buffer_after')?.value || '0';

            setBufferTime({ bufferBefore, bufferAfter });
            // console.log('Buffer:', buffertime);
        } catch (error) {
            console.error('Error fetching buffer time:', error);
        }
    };

    const fetchCalendarData = useCallback(async (therapist, start = null, end = null) => {
        try {
            const params = {
                therapist_id: therapist,
                with: 'therapist.media,therapist.zones,calendars_managements_slots.slot',
            };

            if (start && end) {
                params.date = `between:${start},${end}`;
            }

            const response = await CalendarManagementService.index(params, api,
                auth?.accessToken ? false : true);
            // console.log('response calendrier :', response);
            const filteredDates = [];
            response.forEach(entry => {
                if (entry.date && entry.calendars_managements_slots.length > 0 && entry.calendars_managements_slots.some(slot => slot.status === 'AVAILABLE' || slot.status === 'OVER_TIME')) {
                    filteredDates.push(entry.date);
                }
            });
            // console.log('filteredDates', filteredDates);


            return uniqueAvailabilityDates(filteredDates);

        } catch (error) {
            console.error('Erreur lors de la récupération des données du calendrier :', error);
            throw error;
        }
    }, [api, auth?.accessToken]);

    const fetchBookableDates = useCallback(async (therapistId, start, end) => {
        const flatAddress = address?.address ? address.address : address;
        const latToUse = flatAddress?.lat ?? flatAddress?.latitude;
        const lngToUse = flatAddress?.lng ?? flatAddress?.longitude;
        const zipToUse = flatAddress?.zip_code ?? zip_code;
        const selectedProductIds = selectedServices
            ?.map(service => service?.product?.id)
            .filter(Boolean) || [];
        const cacheKey = [
            start,
            end,
            therapistId || '',
            selectedProductIds.join(','),
            totalDuration || '',
            zipToUse || '',
            latToUse ?? '',
            lngToUse ?? '',
        ].join('|');

        const cached = bookableDatesCacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < 30000) {
            return cached.dates;
        }

        const inFlight = bookableDatesInFlightRef.current.get(cacheKey);
        if (inFlight) {
            return inFlight;
        }

        const request = (async () => {
            const candidateDates = uniqueAvailabilityDates(await fetchCalendarData(therapistId, start, end));

            if (!candidateDates.length) {
                return [];
            }

            if (!therapistId || !selectedProductIds.length || latToUse == null || lngToUse == null || !zipToUse || !totalDuration) {
                return candidateDates;
            }

            const availabilityPayload = {
                dates: candidateDates.map(normalizeAvailabilityDate).filter(Boolean),
                duration: totalDuration,
                lng: lngToUse,
                lat: latToUse,
                zip_code: zipToUse,
                products: selectedProductIds,
                therapists: [therapistId],
                is_hotel: false,
            };

            if (ENABLE_FLYPRO_AVAILABILITY_BATCH && availabilityPayload.dates.length) {
                try {
                    const availabilityByDate = await AvailableSlotsService.batch(
                        availabilityPayload,
                        api,
                        { loaderKey: 'flypro-date-availability' },
                    );
                    const bookableDates = candidateDates.filter((candidateDate) => {
                        const date = normalizeAvailabilityDate(candidateDate);
                        return date && hasAnyBookableSlot(availabilityByDate?.[date]);
                    });
                    bookableDatesCacheRef.current.set(cacheKey, {
                        timestamp: Date.now(),
                        dates: bookableDates,
                    });
                    return bookableDates;
                } catch (error) {
                    console.warn('Batch availability check failed, falling back to per-day checks:', error);
                }
            }

            const checks = await mapWithConcurrency(candidateDates, 3, async (candidateDate) => {
                const date = normalizeAvailabilityDate(candidateDate);
                if (!date) {
                    return null;
                }

                try {
                    const availability = await AvailableSlotsService.store(
                        {
                            date,
                            duration: totalDuration,
                            lng: lngToUse,
                            lat: latToUse,
                            zip_code: zipToUse,
                            products: selectedProductIds,
                            therapists: [therapistId],
                            is_hotel: false,
                        },
                        api,
                        null,
                        { loaderKey: `flypro-date-availability-${date}` },
                    );

                    return hasAnyBookableSlot(availability) ? candidateDate : null;
                } catch (error) {
                    console.error('Error validating favorite flypro bookable date:', date, error);
                    return null;
                }
            });

            const bookableDates = checks.filter(Boolean);
            bookableDatesCacheRef.current.set(cacheKey, {
                timestamp: Date.now(),
                dates: bookableDates,
            });
            return bookableDates;
        })();

        bookableDatesInFlightRef.current.set(cacheKey, request);

        try {
            return await request;
        } finally {
            bookableDatesInFlightRef.current.delete(cacheKey);
        }
    }, [address, api, fetchCalendarData, selectedServices, totalDuration, zip_code]);

    const refreshUnavailableDates = useCallback(async ({ force = false } = {}) => {
        const therapistId = selectedTherapist?.[0]?.id;
        if (!therapistId) {
            return;
        }

        if (isFetchingUnavailableDates.current) {
            return;
        }

        const windowStart = (daysToShow && daysToShow.length)
            ? daysToShow[0]
            : (selectedDateScroll || selectedDate || new Date());
        const windowStartKey = windowStart instanceof Date
            ? windowStart.toDateString()
            : new Date(windowStart).toDateString();
        const flatAddress = address?.address ? address.address : address;
        const selectedProductIds = selectedServices
            ?.map(service => service?.product?.id)
            .filter(Boolean) || [];

        const currentDeps = {
            therapistId,
            windowStartKey,
            selectedProductIds: selectedProductIds.join(','),
            lat: flatAddress?.lat ?? flatAddress?.latitude ?? null,
            lng: flatAddress?.lng ?? flatAddress?.longitude ?? null,
            zip: flatAddress?.zip_code ?? zip_code ?? null,
            totalDuration,
        };
        const depsChanged = Object.keys(currentDeps).some(
            key => prevUnavailableDeps.current[key] !== currentDeps[key]
        );

        if (!force && !depsChanged) {
            return;
        }

        const now = Date.now();
        if (force && now - lastUnavailableRefreshRef.current < 1500) {
            return;
        }

        prevUnavailableDeps.current = currentDeps;
        lastUnavailableRefreshRef.current = now;
        isFetchingUnavailableDates.current = true;

        try {
            const startDate = formatLocalDate(windowStart);
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + (daysToShow?.length || 9));
            const formattedEndDate = formatLocalDate(endDate);
            const requestId = ++unavailableDatesRequestRef.current;
            const dates = await fetchBookableDates(therapistId, startDate, formattedEndDate);
            if (requestId === unavailableDatesRequestRef.current) {
                const formattedDates = dates.map(toDisplayDateString).filter(Boolean);
                setUnavailableDates(formattedDates);
            }
        } catch (error) {
            console.error('Error fetching unavailable dates:', error);
        } finally {
            isFetchingUnavailableDates.current = false;
        }
    }, [address, daysToShow, fetchBookableDates, selectedDate, selectedDateScroll, selectedServices, selectedTherapist, totalDuration, zip_code]);

    useEffect(() => {
        refreshUnavailableDates();
    }, [refreshUnavailableDates]);

    useEffect(() => {
        fetchBufferTime();
    }, []);

    const refreshAvailableHours = useCallback(async ({ force = false } = {}) => {
        const therapistId = selectedTherapist?.[0]?.id;
        if (!selectedDate || !therapistId || !selectedServices?.length || !address) {
            return;
        }

        const flatAddress = address?.address ? address.address : address;
        const latToUse = flatAddress?.lat ?? flatAddress?.latitude;
        const lngToUse = flatAddress?.lng ?? flatAddress?.longitude;
        const zipToUse = flatAddress?.zip_code ?? zip_code;
        if (latToUse == null || lngToUse == null || !zipToUse) {
            return;
        }

        const selectedProductIds = selectedServices
            .map(service => service?.product?.id)
            .filter(Boolean);
        if (!selectedProductIds.length) {
            return;
        }

        const currentDeps = {
            selectedDate: selectedDate?.toDateString(),
            therapistId,
            selectedProductIds: selectedProductIds.join(','),
            latToUse,
            lngToUse,
            zipToUse,
            totalDuration,
        };
        const depsChanged = Object.keys(currentDeps).some(
            key => prevAvailableHoursDeps.current[key] !== currentDeps[key]
        );

        if (!force && !depsChanged) {
            return;
        }

        if (isFetchingAvailableHours.current) {
            return;
        }

        prevAvailableHoursDeps.current = currentDeps;
        isFetchingAvailableHours.current = true;

        try {
            const availabilities = await AvailableSlotsService.store(
                {
                    date: new Date(selectedDate).toLocaleDateString('en-CA').split('T')[0],
                    duration: totalDuration,
                    lng: lngToUse,
                    lat: latToUse,
                    zip_code: zipToUse,
                    products: selectedProductIds,
                    therapists: [therapistId],
                    is_hotel: false
                },
                api,
            );
            setAvailableHours(availabilities);
        } catch (error) {
            console.error('Error fetching available hours:', error);
        } finally {
            isFetchingAvailableHours.current = false;
        }
    }, [selectedDate, selectedTherapist, selectedServices, address, zip_code, totalDuration, api]);

    useEffect(() => {
        refreshAvailableHours();
    }, [refreshAvailableHours]);

    const refreshAvailability = useCallback(async () => {
        const therapistId = selectedTherapist?.[0]?.id;
        if (!therapistId) {
            return;
        }

        if (realtimeRefreshInFlightRef.current) {
            return;
        }

        const now = Date.now();
        if (now - realtimeLastRefreshRef.current < 1500) {
            return;
        }

        realtimeLastRefreshRef.current = now;
        realtimeRefreshInFlightRef.current = true;

        try {
            await refreshUnavailableDates({ force: true });
            if (selectedDate) {
                await refreshAvailableHours({ force: true });
            }
        } catch (error) {
            console.error("Realtime availability refresh failed:", error);
        } finally {
            realtimeRefreshInFlightRef.current = false;
        }
    }, [selectedTherapist, selectedDate, refreshUnavailableDates, refreshAvailableHours]);

    useEffect(() => {
        refreshAvailabilityRef.current = refreshAvailability;
    }, [refreshAvailability]);

    useEffect(() => {
        initPusher({ token: auth?.accessToken });

        const handler = (data) => {
            const payload =
                typeof data === "string"
                    ? (() => {
                        try {
                            return JSON.parse(data);
                        } catch (_) {
                            return {};
                        }
                    })()
                    : data || {};

            const key = payload?.key;
            if (key !== "availability" && key !== "calendar") {
                return;
            }

            const therapistId = selectedTherapist?.[0]?.id;
            if (!therapistId) {
                return;
            }

            const eventTherapists = Array.isArray(payload?.payload?.therapist_ids)
                ? payload.payload.therapist_ids.map((id) => Number(id))
                : payload?.payload?.therapist_id
                    ? [Number(payload.payload.therapist_id)]
                    : [];

            if (eventTherapists.length && !eventTherapists.includes(Number(therapistId))) {
                return;
            }

            refreshAvailabilityRef.current?.();
        };

        const unbind = bindEvent("public-data", "realtime.data.updated", handler);

        return () => {
            unbind();
        };
    }, [auth?.accessToken, selectedTherapist]);

    useEffect(() => {
        initPusher({ token: auth?.accessToken });
        const pusher = getPusher();
        if (!pusher) return;

        const handleConnected = () => {
            refreshAvailabilityRef.current?.();
        };

        pusher.connection.bind("connected", handleConnected);

        return () => {
            pusher.connection.unbind("connected", handleConnected);
        };
    }, [auth?.accessToken]);

    useEffect(() => {
        const handleOnline = () => {
            refreshAvailabilityRef.current?.();
        };

        window.addEventListener("online", handleOnline);

        return () => {
            window.removeEventListener("online", handleOnline);
        };
    }, []);
    const fetchAvailableTherapists = async () => {
        const flatAddress = address?.address ? address.address : address;
        if (!selectedDate || !selectedTime || !flatAddress || !selectedServices?.length) {
            return [];
        }

        const selectedProductIds = selectedServices
            .map(service => service?.product?.id)
            .filter(Boolean);

        try {
            const response = await TherapistService.store(
                {
                    date: formatLocalDate(selectedDate),
                    time: selectedTime,
                    duration: totalDuration,
                    lng: flatAddress.lng,
                    lat: flatAddress.lat,
                    zip_code: flatAddress.zip_code,
                    products: selectedProductIds,
                    is_duo: duo,
                    status: 'ACTIVE',
                },
                api
            );

            if (duo && response && typeof response === 'object' && !Array.isArray(response)) {
                const flattened = Object.values(response).flat();
                return Array.from(new Map(flattened.map(t => [t.id, t])).values());
            }

            return Array.isArray(response) ? response : [];
        } catch (error) {
            console.error('Error checking therapists availability:', error);
            return [];
        }
    };

    useEffect(() => {
        const storedState = JSON.parse(sessionStorage.getItem('state') ?? '{}');
        const now = new Date();
        if (storedState.selectedTherapist) {
            setState(storedState);
        }
    }, []);

    useEffect(() => {
        if (location.state) {
            setState(location.state);
            sessionStorage.setItem('state', JSON.stringify(location.state));
        }
    }, [location.state]);

    useEffect(() => {
        if (!selectedServices || selectedServices.length === 0) {
            navigate('/flypro-service');
        } else if (!address) {
            navigate('/location-pro');
        }
    }, [selectedServices, address, navigate]);

    const handleSubmit = async (event) => {
        event?.preventDefault();
        if (selectedDate.getDay() === 0) {
            setShowPopup(true);
            return;
        }
        const availableTherapists = await fetchAvailableTherapists();
        const selectedTherapistIds = selectedTherapist.map(t => t?.id).filter(Boolean);
        const therapistsStillAvailable = selectedTherapistIds.length > 0 &&
            selectedTherapistIds.every(id => availableTherapists.some(th => th.id === id));

        if (!therapistsStillAvailable) {
            openModalPaiement(`Le flypro que vous avez choisi n’est malheureusement plus disponible pour ce créneau. Nous vous invitons à revenir en arrière et à choisir un autre créneau ou un autre flypro pour que votre soin se déroule dans les meilleures conditions. Merci de votre compréhension.`);
            console.warn(t('unavailable_therapist'));
            return;
        }

        const flatAddress = address?.address ? address.address : address;
        const paymentState = {
            address: flatAddress,
            duo,
            totalDuration,
            totalPrice,
            selectedServices,
            selectedTherapist,
            selectedDate: selectedDate.toLocaleDateString('en-CA'),
            selectedTime,
            phone: phone ?? auth?.user?.phone,
            origin_flow: 'pro',
        };

        sessionStorage.setItem('state', JSON.stringify(paymentState));
        sessionStorage.setItem('previousPath', location.pathname);
        const newBooking = {
            ...(JSON.parse(sessionStorage.getItem("newBooking") || "{}")),
            date: formatLocalDate(selectedDate),
            time: selectedTime
        };
        sessionStorage.setItem("newBooking", JSON.stringify(newBooking));

        if (!auth || Object.keys(auth).length === 0) {
            sessionStorage.setItem('redirectAfterLogin', '/flypro-booking');
        }
        navigate('/payment', {
            state: paymentState,
        });
    };

    const handleMonthChange = (direction) => {
        const now = Date.now();
        if (window.lastMonthChange && (now - window.lastMonthChange) < 300) return;
        window.lastMonthChange = now;
        
        const currentDate = selectedDate || selectedDateScroll;
        if (!currentDate || isNaN(currentDate.getTime())) {
            console.error('Invalid date in handleMonthChange');
            return;
        }
                const maxVisibleMonths = 2;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const firstAllowedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const lastAllowedMonth = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths, 1);
                const newDate = new Date(currentDate);
                newDate.setDate(1);
                newDate.setMonth(newDate.getMonth() + direction);
                newDate.setHours(0, 0, 0, 0);
                if (newDate < firstAllowedMonth) return;
                if (newDate > lastAllowedMonth) return;
                if (newDate.getFullYear() === today.getFullYear() && newDate.getMonth() === today.getMonth()) {
                    newDate.setDate(today.getDate());
                } else {
                    newDate.setDate(1);
                }
                const lastAllowedDate = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths + 1, 0);
                if (newDate > lastAllowedDate) newDate.setTime(lastAllowedDate.getTime());

                if (selectedDate) setSelectedDate(newDate);
                setSelectedDateScroll(newDate);
                updateDaysToShow(newDate);
    };

    const handleDayChange = (direction) => {
        const now = Date.now();
        if (window.lastDayChange && (now - window.lastDayChange) < 200) return;
        window.lastDayChange = now;
        
        const currentDate = selectedDate || selectedDateScroll;
        if (!currentDate || isNaN(currentDate.getTime())) {
            console.error('Invalid date in handleDayChange');
            return;
        }

        const container = document.querySelector('.row-date');
        if (!container) return;

        const dayWidth = container.querySelector('.btn-day')?.offsetWidth || 0;
        const containerWidth = container.clientWidth;
        const visibleItems = Math.floor(containerWidth / dayWidth);
                const daysToScroll = Math.max(1, Math.floor(visibleItems - 0.5));
                const maxVisibleMonths = 2;
                const windowSize = 9;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const endExclusive = new Date(today.getFullYear(), today.getMonth() + maxVisibleMonths + 1, 1);
                const allDays = [];
                let cur = new Date(today);
                while (cur < endExclusive) {
                    allDays.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate()));
                    cur.setDate(cur.getDate() + 1);
                }
                const firstVisible = daysToShow && daysToShow.length ? daysToShow[0] : (selectedDate || today);
                let currentStartIndex = allDays.findIndex(d => d.getTime() === new Date(firstVisible.getFullYear(), firstVisible.getMonth(), firstVisible.getDate()).getTime());
                if (currentStartIndex === -1) currentStartIndex = Math.max(0, allDays.length - windowSize);

                const maxStart = Math.max(0, allDays.length - windowSize);
                let newStart = currentStartIndex + direction * daysToScroll;
                if (newStart < 0) newStart = 0;
                if (newStart > maxStart) newStart = maxStart;

                const newWindow = allDays.slice(newStart, newStart + windowSize);
                setDaysToShow(newWindow);
                const newSelected = newWindow.length ? newWindow[0] : (allDays[allDays.length - 1] || today);
                if (selectedDate) setSelectedDate(newSelected);
                setSelectedDateScroll(newSelected);
                setSelectedTime(null);
                updateDaysToShow(newSelected);

    };
    const redirectToPartnerHotels = () => {
        window.location.href = '/hotels';
    };

    function handleDayClick(date) {
        setSelectedDate(date);

        const now = new Date();
        let initialTime;

        if (date.getDay() === 0) {
            setShowPopup(true);
        }

        if (date?.toDateString() === now?.toDateString()) {
            const nextQuarterHour = Math.ceil(now.getMinutes() / 15) * 15;
            now.setMinutes(nextQuarterHour);
            now.setSeconds(0);
            initialTime = now;
        } else {
            initialTime = new Date(date);
            initialTime.setHours(8, 0, 0, 0);
        }

        setSelectedTime(null);
    }
    function handleHourClick(hour) {
        setSelectedTime(hour);
    }

    const getMonthName = (date) => {
        if (!date || isNaN(date.getTime())) {
            const fallback = new Date().toLocaleString(i18n.language, { month: 'long' });
            return fallback.charAt(0).toUpperCase() + fallback.slice(1);
        }
        const monthName = date.toLocaleString(i18n.language, { month: 'long' });
        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    };

    const getDayName = (date) => {
        if (!date || isNaN(date.getTime())) {
            return new Date().toLocaleString(i18n.language, { weekday: 'short' });
        }
        return date.toLocaleString(i18n.language, { weekday: 'short' });
    };

    const scrollLeft = () => {
        const container = document.querySelector('.row-day');
        if (!container) return;

        const dayWidth = container.querySelector('.btn-hour')?.offsetWidth || 0;
        const visibleItems = Math.floor(container.clientWidth / dayWidth);
        const scrollDistance = dayWidth * (visibleItems - 0.5);

        container.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
    };

    const scrollRight = () => {
        const container = document.querySelector('.row-day');
        if (!container) return;

        const dayWidth = container.querySelector('.btn-hour')?.offsetWidth || 0;
        const visibleItems = Math.floor(container.clientWidth / dayWidth);
        const scrollDistance = dayWidth * (visibleItems - 0.5);

        container.scrollBy({ left: scrollDistance, behavior: 'smooth' });
    };
    const getNextHourSlots = (date) => {
        const now = new Date();
        const hours = [];

        if (date?.toDateString() === now?.toDateString()) {
            const minutes = now.getMinutes();
            const nextQuarterHour = Math.ceil(minutes / 15) * 15;
            now.setMinutes(nextQuarterHour);
            now.setSeconds(0);

            for (let i = 0; i < 57; i++) {
                const slot = new Date(now.getTime() + i * 15 * 60000);
                if (slot.getHours() >= 8 && slot.getHours() < 21) {
                    hours.push(slot.toLocaleTimeString('en-CA', { timeZone: 'Europe/Zurich', hour: '2-digit', minute: '2-digit', hour12: false }));
                }
            }
            // // console.log('hours1',hours)

        } else {
            const startOfDay = new Date(date);
            startOfDay.setHours(8, 0, 0, 0);

            for (let i = 0; i < 57; i++) {
                const slot = new Date(startOfDay.getTime() + i * 15 * 60000);
                if (slot.getHours() >= 8 && slot.getHours() < 22) {
                    hours.push(slot.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false }));
                }
            }
            // // console.log('hours2',hours)
        }

        return hours;
    };

    const hours = getNextHourSlots(selectedDate);
    return (
        <div className="flypro-booking-page">
            <Header className="header--flypro-flow" compactLanguage />

            <form className="form-masseur" onSubmit={handleSubmit}>
                <div className='container container-therapeut flypro-booking-page__intro'>
                    <h2 style={{ textAlign: 'center', fontWeight: 700, color: '#000000' }}>{t('flyProBooking:choose_date')}</h2>
                </div>

                <div className='container container-therapeut'>
                    <DateTimeSelectorFavPro
                        selectedDate={selectedDate}
                        selectedDateScroll={selectedDateScroll}
                        setSelectedDate={setSelectedDate}
                        selectedTime={selectedTime}
                        setSelectedTime={setSelectedTime}
                        handleMonthChange={handleMonthChange}
                        handleDayChange={handleDayChange}
                        handleDayClick={handleDayClick}
                        handleHourClick={handleHourClick}
                        daysToShow={daysToShow}
                        hours={hours}
                        totalPrice={totalPrice}
                        scrollLeft={scrollLeft}
                        scrollRight={scrollRight}
                        getMonthName={getMonthName}
                        getDayName={getDayName}
                        unavailableDates={unavailableDates}
                        availableHours={availableHours}
                        buffertime={buffertime}
                        totalDuration={totalDuration}
                        traveldistance={traveldistance}
                    />
                </div>

                <div className='btn-bottom'>
                    <button type="submit" className="btn btn-primary">
                        {t('flyProBooking:next')}
                    </button>
                </div>

            </form>
            {showPopup && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <p>{t('flyProBooking:no_service_sunday')}</p>
                        <button onClick={redirectToPartnerHotels} className="btn redirect-button">
                            {t('flyProBooking:choose_partner_hotel')}
                        </button>
                        <button onClick={() => setShowPopup(false)} className="btn close-button">
                            {t('flyProBooking:close')}
                        </button>
                    </div>
                </div>
            )}
            <Modal
                isOpen={modalIsPaiementOpen}
                onRequestClose={closeModalPaiement}
                style={customStyles2}
                contentLabel="Paiement Modal"
                id="custom-popup-content"
            >
                <div className="contentModal">
                    <div className="modal">
                        <p className="modal-text2 text-message" style={{ whiteSpace: 'pre-line' }}>
                            {modalMessage}
                        </p>
                        <div className="modal-button-container">
                            <button className="modal-ok-button" onClick={redirectToHome}>
                                OK
                            </button>
                            <button className="modal-cancel-button" onClick={closeModalPaiement}>
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default FlyProBooking;
